import React, { useState, useEffect } from 'react';
import { shipService, Ship, Manufacturer, Size, Role, ProductionStatus, ShipStats } from '../services/shipService';
import ShipCard from '../components/ships/ShipCard';
import ShipFilters from '../components/ships/ShipFilters';
import ShipListView, { SortField } from '../components/ships/ShipListView';
import { LayoutGrid, List, ArrowUpDown, ChevronDown, Globe, Info } from 'lucide-react';
import styles from './ShipDatabase.module.css';
import sharedStyles from '../styles/shared.module.css';

type ViewMode = 'grid' | 'list';

const ShipDatabase: React.FC = () => {
  const [ships, setShips] = useState<Ship[]>([]);
  const [manufacturers, setManufacturers] = useState<Manufacturer[]>([]);
  const [sizes, setSizes] = useState<Size[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [productionStatuses, setProductionStatuses] = useState<ProductionStatus[]>([]);
  const [stats, setStats] = useState<ShipStats | null>(null);
  const [versionInfo, setVersionInfo] = useState<{ scVersion: string; dataSource: string }>({ scVersion: 'Alpha', dataSource: 'LIVE' });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // View mode
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  
  // Sorting (client-side for list view)
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  
  // Quick sort dropdown
  const [showSortDropdown, setShowSortDropdown] = useState(false);

  // Filters
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedManufacturer, setSelectedManufacturer] = useState('');
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedClass, setSelectedClass] = useState('');
  const [shipClasses, setShipClasses] = useState<import('../services/shipService').ShipClass[]>([]);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchFilterData();
    fetchStats();
    fetchVersion();
  }, []);

  // Refetch when filters, pagination, or sorting changes
  useEffect(() => {
    fetchShips();
  }, [page, pageSize, selectedManufacturer, selectedSize, selectedRole, selectedClass, selectedStatus, searchQuery, sortField, sortDirection]);

  // Update page size when view mode changes
  useEffect(() => {
    setPageSize(viewMode === 'list' ? 50 : 12);
    setPage(1);
  }, [viewMode]);

  const fetchFilterData = async () => {
    try {
      const [manufacturersData, sizesData, rolesData, statusesData, classesData] = await Promise.all([
        shipService.getManufacturers(),
        shipService.getSizes(),
        shipService.getRoles(),
        shipService.getProductionStatuses(),
        shipService.getShipClasses()
      ]);
      setShipClasses(classesData);

      setManufacturers(manufacturersData);
      setSizes(sizesData);
      setRoles(rolesData);
      setProductionStatuses(statusesData);
    } catch (err) {
      console.error('Failed to fetch filter data:', err);
    }
  };

  const fetchStats = async () => {
    try {
      const statsData = await shipService.getStats();
      setStats(statsData);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  const fetchVersion = async () => {
    try {
      const versionData = await shipService.getVersion();
      setVersionInfo(versionData);
    } catch (err) {
      console.error('Failed to fetch version:', err);
    }
  };

  const fetchShips = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page,
        pageSize,
        ...(selectedManufacturer && { manufacturer: selectedManufacturer }),
        ...(selectedSize && { size: selectedSize }),
        ...(selectedRole && { role: selectedRole }),
        ...(selectedClass && { shipClass: selectedClass }),
        ...(selectedStatus && { productionStatus: selectedStatus }),
        ...(searchQuery && { search: searchQuery }),
        sortBy: sortField,
        sortOrder: sortDirection
      };

      const response = await shipService.getShips(params);
      setShips(response.ships);
      setTotalPages(response.totalPages);
      setTotalCount(response.totalCount);
    } catch (err: any) {
      console.error('Failed to fetch ships:', err);
      setError(err.response?.data?.error || 'Failed to load ships');
    } finally {
      setLoading(false);
    }
  };

  // Server-side sorting is now used - ships come pre-sorted from API
  const sortedShips = ships;

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setPage(1); // Reset to first page when sorting changes
  };

  const handleQuickSort = (field: SortField, direction: 'asc' | 'desc') => {
    setSortField(field);
    setSortDirection(direction);
    setShowSortDropdown(false);
    setPage(1); // Reset to first page when sorting changes
  };

  const handleClearFilters = () => {
    setSelectedManufacturer('');
    setSelectedSize('');
    setSelectedRole('');
    setSelectedClass('');
    setSelectedStatus('');
    setSearchQuery('');
    setPage(1);
  };

  const handleFilterChange = () => {
    setPage(1); // Reset to first page when filters change
  };

  const quickSortOptions = [
    { label: 'Name (A-Z)', field: 'name' as SortField, direction: 'asc' as const },
    { label: 'Name (Z-A)', field: 'name' as SortField, direction: 'desc' as const },
    { label: 'Fastest Speed', field: 'maxSpeed' as SortField, direction: 'desc' as const },
    { label: 'Slowest Speed', field: 'maxSpeed' as SortField, direction: 'asc' as const },
    { label: 'Most Cargo', field: 'cargoCapacity' as SortField, direction: 'desc' as const },
    { label: 'Least Cargo', field: 'cargoCapacity' as SortField, direction: 'asc' as const },
    { label: 'Largest Crew', field: 'crewMax' as SortField, direction: 'desc' as const },
    { label: 'Smallest Crew', field: 'crewMax' as SortField, direction: 'asc' as const },
    { label: 'Highest Price', field: 'priceAUEC' as SortField, direction: 'desc' as const },
    { label: 'Lowest Price', field: 'priceAUEC' as SortField, direction: 'asc' as const },
    { label: 'Most Hull HP', field: 'hullHealth' as SortField, direction: 'desc' as const },
    { label: 'Size (Small→Large)', field: 'size' as SortField, direction: 'asc' as const },
    { label: 'Size (Large→Small)', field: 'size' as SortField, direction: 'desc' as const },
  ];

  const getCurrentSortLabel = () => {
    const option = quickSortOptions.find(
      o => o.field === sortField && o.direction === sortDirection
    );
    return option?.label || 'Sort By';
  };

  const dataSourceClass = versionInfo.dataSource?.toUpperCase() === 'LIVE'
    ? sharedStyles.dataSourceLive
    : sharedStyles.dataSourcePtu;

  return (
    <div className={styles.container}>
      {/* Community Resource Banner */}
      <div className={styles.communityBanner}>
        <Globe size={20} />
        <span>Free Community Resource — No account required to browse</span>
      </div>

      {/* Development Disclaimer */}
      <div className={styles.developmentBanner}>
        <Info size={18} />
        <span>This page is still under development. There may be bugs and incomplete information.</span>
      </div>
      
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <h1 className={styles.title}>Ship Database</h1>
          <span className={`${sharedStyles.dataSourceBadge} ${dataSourceClass}`}>{versionInfo.dataSource}</span>
          <span className={styles.versionBadge}>{versionInfo.scVersion}</span>
        </div>
        <p className={styles.subtitle}>
          A free Star Citizen ship reference for the community — Powered by TFIC
        </p>

        {stats && (
          <div className={styles.statsBar}>
            <div className={styles.stat}>
              <span className={styles.statValue}>{stats.totalShips}</span>
              <span className={styles.statLabel}>Total Ships</span>
            </div>
            <div className={styles.stat}>
              <span className={styles.statValue}>{stats.manufacturers}</span>
              <span className={styles.statLabel}>Manufacturers</span>
            </div>
            <div className={styles.stat}>
              <span className={styles.statValue}>{stats.flightReady}</span>
              <span className={styles.statLabel}>Flight Ready</span>
            </div>
            <div className={styles.stat}>
              <span className={styles.statValue}>{stats.inConcept}</span>
              <span className={styles.statLabel}>In Concept</span>
            </div>
          </div>
        )}
      </div>

      <ShipFilters
        manufacturers={manufacturers}
        sizes={sizes}
        roles={roles}
        productionStatuses={productionStatuses}
        selectedManufacturer={selectedManufacturer}
        selectedSize={selectedSize}
        selectedRole={selectedRole}
        shipClasses={shipClasses}
        selectedClass={selectedClass}
        onClassChange={(value) => { setSelectedClass(value); handleFilterChange(); }}
        selectedStatus={selectedStatus}
        searchQuery={searchQuery}
        onManufacturerChange={(value) => { setSelectedManufacturer(value); handleFilterChange(); }}
        onSizeChange={(value) => { setSelectedSize(value); handleFilterChange(); }}
        onRoleChange={(value) => { setSelectedRole(value); handleFilterChange(); }}
        onStatusChange={(value) => { setSelectedStatus(value); handleFilterChange(); }}
        onSearchChange={(value) => { setSearchQuery(value); handleFilterChange(); }}
        onClearFilters={handleClearFilters}
      />

      {/* View Controls */}
      <div className={styles.viewControls}>
        <div className={styles.resultsInfo}>
          <p className={styles.resultsText}>
            Showing {sortedShips.length} of {totalCount} ships
          </p>
        </div>

        <div className={styles.controlsRight}>
          {/* Quick Sort Dropdown */}
          <div className={styles.sortDropdownContainer}>
            <button 
              className={styles.sortDropdownTrigger}
              onClick={() => setShowSortDropdown(!showSortDropdown)}
            >
              <ArrowUpDown size={16} />
              <span>{getCurrentSortLabel()}</span>
              <ChevronDown size={14} className={showSortDropdown ? styles.rotated : ''} />
            </button>
            
            {showSortDropdown && (
              <>
                <div 
                  className={styles.sortDropdownOverlay}
                  onClick={() => setShowSortDropdown(false)}
                />
                <div className={styles.sortDropdown}>
                  {quickSortOptions.map((option) => (
                    <button
                      key={`${option.field}-${option.direction}`}
                      className={`${styles.sortOption} ${
                        sortField === option.field && sortDirection === option.direction 
                          ? styles.active 
                          : ''
                      }`}
                      onClick={() => handleQuickSort(option.field, option.direction)}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* View Mode Toggle */}
          <div className={styles.viewToggle}>
            <button
              className={`${styles.viewButton} ${viewMode === 'grid' ? styles.active : ''}`}
              onClick={() => setViewMode('grid')}
              title="Grid View"
            >
              <LayoutGrid size={18} />
            </button>
            <button
              className={`${styles.viewButton} ${viewMode === 'list' ? styles.active : ''}`}
              onClick={() => setViewMode('list')}
              title="List View"
            >
              <List size={18} />
            </button>
          </div>
        </div>
      </div>

      {loading && (
        <div className={styles.loading}>
          <div className={styles.spinner}></div>
          <p>Loading ships...</p>
        </div>
      )}

      {error && (
        <div className={styles.error}>
          <p>⚠️ {error}</p>
          <button onClick={fetchShips} className={styles.retryButton}>
            Try Again
          </button>
        </div>
      )}

      {!loading && !error && ships.length === 0 && (
        <div className={styles.empty}>
          <p>No ships found matching your filters.</p>
          <button onClick={handleClearFilters} className={styles.clearButton}>
            Clear Filters
          </button>
        </div>
      )}

      {!loading && !error && ships.length > 0 && (
        <>
          {viewMode === 'grid' ? (
            <div className={styles.grid}>
              {sortedShips.map((ship) => (
                <ShipCard key={ship.id} ship={ship} />
              ))}
            </div>
          ) : (
            <ShipListView
              ships={sortedShips}
              sortField={sortField}
              sortDirection={sortDirection}
              onSort={handleSort}
              onFilterByManufacturer={(manufacturer) => {
                setSelectedManufacturer(manufacturer);
                setPage(1);
              }}
              onFilterByRole={(role) => {
                setSelectedRole(role);
                setPage(1);
              }}
              onFilterBySize={(size) => {
                setSelectedSize(size);
                setPage(1);
              }}
            />
          )}

          {totalPages > 1 && (
            <div className={styles.pagination}>
              <button
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                className={styles.pageButton}
              >
                ← Previous
              </button>

              <div className={styles.pageNumbers}>
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (page <= 3) {
                    pageNum = i + 1;
                  } else if (page >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = page - 2 + i;
                  }

                  return (
                    <button
                      key={pageNum}
                      onClick={() => setPage(pageNum)}
                      className={`${styles.pageNumber} ${page === pageNum ? styles.active : ''}`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => setPage(page + 1)}
                disabled={page === totalPages}
                className={styles.pageButton}
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}
      
      {/* Disclaimer */}
      <div className={styles.disclaimer}>
        <Info size={16} />
        <p>
          This is an unofficial fan-made project and is not affiliated with or endorsed by Cloud Imperium Games 
          or Roberts Space Industries. Star Citizen® and all associated marks are the property of their respective owners.
        </p>
      </div>

      {/* Support Banner */}
      <div className={styles.supportBanner}>
        <p>Enjoy this free resource? Consider supporting development!</p>
        <a 
          href="https://ko-fi.com/digitalhorizonlabs" 
          target="_blank" 
          rel="noopener noreferrer"
          className={styles.kofiButton}
        >
          ☕ Support on Ko-fi
        </a>
      </div>
    </div>
  );
};

export default ShipDatabase;
